/**
 * Authentication Provider Component
 * Wraps the application with MSAL context and provides auth state via useAuthContext
 * Supports both standard browser and Teams NAA contexts
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import {
  EventType,
  InteractionRequiredAuthError,
  type EventMessage,
  type AuthenticationResult,
  type IPublicClientApplication,
} from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { bootstrapAuth, getTeamsContext } from './bootstrap'
import { getLoginRequest, getTokenRequest } from './authConfig'

/** Authentication context state */
interface AuthContextState {
  /** Whether running in Teams context */
  isTeams: boolean
  /** Whether NAA is enabled (Teams with nestable PCA) */
  isNAAEnabled: boolean
  /** Whether user interaction is required for authentication */
  requiresInteraction: boolean
  /** Whether silent authentication has been attempted */
  silentAuthAttempted: boolean
  /** Whether currently authenticated */
  isAuthenticated: boolean
  /** Trigger interactive login (popup) */
  triggerInteractiveLogin: () => Promise<void>
}

const AuthContext = createContext<AuthContextState | null>(null)

/**
 * Hook to access authentication context
 * @throws Error if used outside of AuthProvider
 */
export function useAuthContext(): AuthContextState {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

/**
 * Authentication provider component that wraps the application with MSAL context
 * Handles async initialization, redirect promise, silent auth, and account management
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [msalInstance, setMsalInstance] = useState<IPublicClientApplication | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [isTeams, setIsTeams] = useState(false)
  const [requiresInteraction, setRequiresInteraction] = useState(false)
  const [silentAuthAttempted, setSilentAuthAttempted] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  // Initialize MSAL
  useEffect(() => {
    const initialize = async () => {
      try {
        // Bootstrap auth (handles Teams detection and MSAL creation)
        const instance = await bootstrapAuth()
        setMsalInstance(instance)

        // Get Teams context
        const teamsContext = getTeamsContext()
        setIsTeams(teamsContext.isTeams)

        // Register event callback for login/logout
        instance.addEventCallback((event: EventMessage) => {
          if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const authResult = event.payload as AuthenticationResult
            if (authResult.account) {
              instance.setActiveAccount(authResult.account)
              setIsAuthenticated(true)
              setRequiresInteraction(false)
            }
          } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
            setIsAuthenticated(false)
            setRequiresInteraction(false)
            setSilentAuthAttempted(false)
          }
        })

        // Check if already authenticated
        const account = instance.getActiveAccount()
        if (account) {
          setIsAuthenticated(true)
        }

        setIsInitialized(true)

        // Attempt silent token acquisition
        if (account) {
          try {
            await instance.acquireTokenSilent({
              ...getTokenRequest(),
              account,
            })
            console.log('[Auth] Silent token acquisition successful')
            setIsAuthenticated(true)
          } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
              console.log('[Auth] Interaction required for token acquisition')
              setRequiresInteraction(true)
              setIsAuthenticated(false)
            } else {
              console.error('[Auth] Silent token acquisition failed:', error)
              setIsAuthenticated(false)
              setRequiresInteraction(true)
            }
          }
          setSilentAuthAttempted(true)
        } else {
          // No account, interaction will be required
          setRequiresInteraction(true)
          setSilentAuthAttempted(true)
        }
      } catch (error) {
        console.error('[Auth] Failed to initialize auth:', error)
        setIsInitialized(true) // Still set to true to prevent infinite loading
        setSilentAuthAttempted(true)
        setRequiresInteraction(true)
      }
    }

    initialize()
  }, [])

  // Interactive login handler
  const triggerInteractiveLogin = useCallback(async () => {
    if (!msalInstance) {
      throw new Error('MSAL not initialized')
    }

    try {
      // Always use popup for Teams compatibility
      const result = await msalInstance.loginPopup(getLoginRequest())
      if (result.account) {
        msalInstance.setActiveAccount(result.account)
        setIsAuthenticated(true)
        setRequiresInteraction(false)
      }
    } catch (error) {
      console.error('[Auth] Interactive login failed:', error)
      throw error
    }
  }, [msalInstance])

  // Show nothing while initializing
  if (!isInitialized || !msalInstance) {
    return null
  }

  const contextValue: AuthContextState = {
    isTeams,
    isNAAEnabled: isTeams, // NAA is enabled when in Teams context
    requiresInteraction,
    silentAuthAttempted,
    isAuthenticated,
    triggerInteractiveLogin,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      <MsalProvider instance={msalInstance}>{children}</MsalProvider>
    </AuthContext.Provider>
  )
}

// Re-export MSAL instance getter for services
export { getMsalInstance as msalInstance } from './bootstrap'
